import { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  PasswordInput,
  Button,
  Group,
  Stack,
  Text,
  Alert,
  Badge,
  Divider,
} from '@mantine/core';
import { IconKey, IconUser, IconAlertCircle, IconTrash } from '@tabler/icons-react';
import {
  useDeviceCredentials,
  useSetDeviceCredentials,
  useClearDeviceCredentials,
} from '@/hooks/useRemoteManagement';

interface Device {
  id: string;
  hostname: string | null;
  name?: string;
}

interface DeviceCredentialsModalProps {
  opened: boolean;
  onClose: () => void;
  device: Device | null;
}

export function DeviceCredentialsModal({
  opened,
  onClose,
  device,
}: DeviceCredentialsModalProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const { data: credentials, isLoading: isLoadingCredentials } = useDeviceCredentials(
    device?.id || ''
  );
  const setCredentials = useSetDeviceCredentials();
  const clearCredentials = useClearDeviceCredentials();

  // Reset form when device changes or modal opens
  useEffect(() => {
    if (opened && credentials) {
      setUsername(credentials.username || '');
      setPassword('');
      setConfirmPassword('');
    }
  }, [opened, credentials]);

  const handleSubmit = () => {
    if (!device) return;

    if (password !== confirmPassword) {
      return;
    }

    setCredentials.mutate(
      {
        deviceId: device.id,
        username,
        password,
      },
      {
        onSuccess: () => {
          setPassword('');
          setConfirmPassword('');
          onClose();
        },
      }
    );
  };

  const handleClear = () => {
    if (!device) return;

    clearCredentials.mutate(device.id, {
      onSuccess: () => {
        setUsername('');
        setPassword('');
        setConfirmPassword('');
        onClose();
      },
    });
  };

  const isPasswordMismatch = password !== confirmPassword && confirmPassword.length > 0;
  const canSubmit =
    username.length > 0 &&
    password.length > 0 &&
    password === confirmPassword &&
    !setCredentials.isPending;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconKey size={20} />
          <Text fw={600}>WinRM Credentials</Text>
        </Group>
      }
      size="md"
    >
      {device && (
        <Stack gap="md">
          <Alert variant="light" color="blue">
            <Text size="sm">
              Setting credentials for: <strong>{device.hostname || device.name || device.id}</strong>
            </Text>
          </Alert>

          {!isLoadingCredentials && credentials?.hasCredentials && (
            <Group gap="xs">
              <Badge color="green" variant="light">
                Credentials configured
              </Badge>
              <Text size="sm" c="dimmed">
                User: {credentials.username}
              </Text>
            </Group>
          )}

          {!isLoadingCredentials && !credentials?.hasCredentials && (
            <Badge color="gray" variant="light">
              No credentials configured
            </Badge>
          )}

          <Divider label="Set New Credentials" labelPosition="center" />

          <TextInput
            label="Username"
            placeholder="administrator"
            leftSection={<IconUser size={16} />}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            description="Windows username (e.g., administrator, DOMAIN\\user)"
          />

          <PasswordInput
            label="Password"
            placeholder="Enter password"
            leftSection={<IconKey size={16} />}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <PasswordInput
            label="Confirm Password"
            placeholder="Re-enter password"
            leftSection={<IconKey size={16} />}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            error={isPasswordMismatch ? 'Passwords do not match' : undefined}
          />

          {isPasswordMismatch && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              Passwords do not match
            </Alert>
          )}

          <Group justify="space-between" mt="md">
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={handleClear}
              disabled={!credentials?.hasCredentials || clearCredentials.isPending}
              loading={clearCredentials.isPending}
            >
              Clear Credentials
            </Button>

            <Group>
              <Button variant="default" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!canSubmit}
                loading={setCredentials.isPending}
              >
                Save Credentials
              </Button>
            </Group>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
